import authSession from "../../lib/authSession";
import { API_URL } from "../../constants";
import { safeFetch, explainApiError } from "../../utils/toDisplayString";

export const resourcePersonJournalInitialState = {
  journals: [],
  journalsLoading: false,
  journalsError: null,
  journalsTotal: 0,
  journalsPages: 1,
  journalDetail: null,
  journalDetailLoading: false,
  journalDetailError: null,
  journalDownloads: [],
  journalDownloadsLoading: false,
  journalDownloadsError: null,
  journalAnalytics: null,
  journalAnalyticsLoading: false,
  journalAnalyticsError: null,
  journalMutationLoading: false,
  journalMutationError: null,
};

function buildUrl(path, params = {}) {
  const query = new URLSearchParams(Object.entries(params).filter(([, value]) => value !== undefined && value !== "")).toString();
  return `${API_URL.replace(/\/$/, "")}${path}${query ? `?${query}` : ""}`;
}

async function request(path, { method = "GET", params, body } = {}) {
  if (!API_URL) throw new Error("REACT_APP_API_BASE_URL is not configured.");
  const token = authSession.getToken();
  const response = await safeFetch(buildUrl(path, params), {
    method,
    headers: { Accept: "application/json", ...(body !== undefined ? { "Content-Type": "application/json" } : {}), ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    credentials: "omit",
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });
  const text = await response.text();
  let data = {};
  if (text) {
    try { data = JSON.parse(text); } catch { throw new Error(`Journals API returned a non-JSON response (${response.status}).`); }
  }
  if (!response.ok || data.error || data.success === false) throw new Error(explainApiError(response.status, data || {}, `Request failed with status ${response.status}.`));
  return data?.data && typeof data.data === "object" ? data.data : data;
}

function pageOf(data) {
  const items = Array.isArray(data) ? data : data?.items || data?.journals || [];
  return { items, total: data?.total ?? data?.totalItems ?? items.length, pages: data?.totalPages ?? data?.pages ?? 1 };
}

function listOf(data, keys) {
  if (Array.isArray(data)) return data;
  for (const key of keys) if (Array.isArray(data?.[key])) return data[key];
  return [];
}

function journalOf(data) {
  return data?.journal && typeof data.journal === "object" ? data.journal : data;
}

/**
 * resourcePersonJournalStore — journal authoring slice.
 *
 *   GET    /api/resource-person/journals
 *   POST   /api/resource-person/journals
 *   GET    /api/resource-person/journals/:id
 *   PATCH  /api/resource-person/journals/:id
 *   PATCH  /api/resource-person/journals/:id/submit
 *   PATCH  /api/resource-person/journals/:id/unsubmit
 *   DELETE /api/resource-person/journals/:id
 *   GET    /api/resource-person/journals/:id/downloads
 *   GET    /api/resource-person/journals/:id/analytics
 */
export function createResourcePersonJournalActions(set) {
  const mutation = async (path, method, body) => {
    set({ journalMutationLoading: true, journalMutationError: null });
    try {
      const journal = journalOf(await request(path, { method, body }));
      set({ journalMutationLoading: false, journalDetail: journal });
      return { success: true, journal };
    } catch (error) {
      set({ journalMutationLoading: false, journalMutationError: error.message });
      return { success: false, error: error.message };
    }
  };

  return {
    fetchJournals: async (params = {}) => {
      set({ journalsLoading: true, journalsError: null });
      try {
        const result = pageOf(await request("/api/resource-person/journals", { params }));
        set({ journals: result.items, journalsTotal: result.total, journalsPages: result.pages, journalsLoading: false });
        return { success: true, ...result };
      } catch (error) {
        set({ journals: [], journalsLoading: false, journalsError: error.message });
        return { success: false, error: error.message };
      }
    },
    createJournal: (payload) => mutation("/api/resource-person/journals", "POST", payload),
    fetchJournalById: async (id) => {
      set({ journalDetailLoading: true, journalDetailError: null });
      try { const journal = journalOf(await request(`/api/resource-person/journals/${id}`)); set({ journalDetail: journal, journalDetailLoading: false }); return { success: true, journal }; }
      catch (error) { set({ journalDetail: null, journalDetailLoading: false, journalDetailError: error.message }); return { success: false, error: error.message }; }
    },
    updateJournal: (id, payload) => mutation(`/api/resource-person/journals/${id}`, "PATCH", payload),
    submitJournal: (id) => mutation(`/api/resource-person/journals/${id}/submit`, "PATCH"),
    unsubmitJournal: (id) => mutation(`/api/resource-person/journals/${id}/unsubmit`, "PATCH"),
    deleteJournal: (id) => mutation(`/api/resource-person/journals/${id}`, "DELETE"),
    fetchJournalDownloads: async (id, params = {}) => {
      set({ journalDownloadsLoading: true, journalDownloadsError: null });
      try { const data = await request(`/api/resource-person/journals/${id}/downloads`, { params }); const items = listOf(data, ["items", "downloads"]); set({ journalDownloads: items, journalDownloadsLoading: false }); return { success: true, downloads: items }; }
      catch (error) { set({ journalDownloads: [], journalDownloadsLoading: false, journalDownloadsError: error.message }); return { success: false, error: error.message }; }
    },
    fetchJournalAnalytics: async (id) => {
      set({ journalAnalyticsLoading: true, journalAnalyticsError: null });
      try { const analytics = await request(`/api/resource-person/journals/${id}/analytics`); set({ journalAnalytics: analytics, journalAnalyticsLoading: false }); return { success: true, analytics }; }
      catch (error) { set({ journalAnalytics: null, journalAnalyticsLoading: false, journalAnalyticsError: error.message }); return { success: false, error: error.message }; }
    },
  };
}
